<template>
  <LoaderWrapper ref="modalLoader" class="h-100">
    <div class="row mt-4">
      <div class="col-sm-12">
        <table class="table table-bordered">
          <thead>
          <tr class="bg-light">
            <th class="text-capitalize-first">{{ helper.trans('labels.traffic_group') }}</th>
            <th class="text-capitalize-first">{{ helper.trans('labels.type') }}</th>
            <th class="text-capitalize-first" style="width: 0px"></th>
            <th class="text-capitalize-first">{{ helper.trans('labels.reward_fee_percent_short')}}</th>
            <th colspan="2" class="text-capitalize-first text-center">{{ helper.trans('labels.min_max_store_reward')}}</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="item in sortedSettings" :key="item.trafficGroupId" :class="{'bg-warning': !isValidRewards(item)}">
            <td class="align-middle bg-white">{{item._trafficGroup?.name}} <i v-if="item._trafficGroup?.isHidden" class="fa fa-eye-slash"></i></td>
            <td class="align-middle bg-white">{{helper.trans(`direction.${item._trafficGroup?.type}`)}}</td>
            <td class="bg-white">
              <div class="form-check form-switch d-flex align-items-center justify-content-center">
                <input v-model="item.enabled" id="autoclose" class="form-check-input" style="cursor: pointer" type="checkbox" role="switch"  >
              </div>
            </td>
            <td style="width: 100px"><input class="form-control form-control-sm" v-model="item._rewardPercent"></td>
            <td style="width: 100px"><input class="form-control form-control-sm" readonly :value="item._groupMinRewardPercent"></td>
            <td style="width: 100px"><input class="form-control form-control-sm" readonly :value="item._groupMaxRewardPercent"></td>
          </tr>
          </tbody>
        </table>

      </div>
    </div>
  </LoaderWrapper>
</template>

<script lang="ts">
import {computed, defineComponent, Prop, PropType, reactive, ref, toRefs, watch} from "vue";
import {useStore} from "vuex";
import {helper} from "@/service/CommonHelper";
import {numberFormatter} from "@/service/NumberFormatter";
import Multiselect from "vue-multiselect";
import {api} from "@/service/APIClient";
import {useNotification} from "@kyvg/vue3-notification";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import {Direction} from "@/models/enum/Direction";
import {Dictionary} from "@/types";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import UserDTO from "@/models/UserDTO";
import TeamLeadRewardSettingsDTO from "@/models/TeamLeadRewardSettingsDTO";


const components = {
  Multiselect,
  FormInputWrapper,
  LoaderWrapper
}

const notification = useNotification()

export default defineComponent({
  name: "TeamLeadRewardSettingsForm",
  props: {
    teamLead: {
      type: Object as PropType<UserDTO|null>,
      default: null
    } as Prop<UserDTO|null>
  },
  components,
  setup(props) {

    const modalLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const store = useStore();
    const state = reactive({
      rewardSettingsByGroupId: {} as Dictionary<TeamLeadRewardSettingsDTO>
    });


    const save = async () => {
      if (!props.teamLead) {
        return;
      }

      let rewardSettings = [];
      for (const settings of Object.values(state.rewardSettingsByGroupId)) {
        rewardSettings.push({
          trafficGroupId: settings.trafficGroupId,
          teamLeadId: settings.teamLeadId,
          reward: settings._rewardPercent ? helper.percentToFloat(settings._rewardPercent) : null,
          enabled: settings.enabled
        })
      }

      await api.call.teamLeadRewardSettings.update(props.teamLead.id, {
        rewardSettings
      })
    }

    watch(() => props.teamLead, async () => {
      if (!props.teamLead) {
        state.rewardSettingsByGroupId = {}
        return;
      }

      modalLoader.value?.show()
      try {
        let rewardSettingsByGroupId = {} as Dictionary<TeamLeadRewardSettingsDTO>
        for (const item of await api.call.teamLeadRewardSettings.get(props.teamLead.id)) {
          rewardSettingsByGroupId[item.trafficGroupId] = item
        }
        for (const group of await api.call.trafficGroups.all()) {
          let settings = rewardSettingsByGroupId[group.id] ?? null
          if (settings === null) {
            settings = {
              trafficGroupId: group.id,
              teamLeadId: props.teamLead.id,
              reward: '',
              enabled: false
            } as TeamLeadRewardSettingsDTO
            rewardSettingsByGroupId[group.id] = settings
          }

          settings._trafficGroup = group
          settings._groupMinRewardPercent = group.minReward ? helper.floatToPercent(group.minReward) : null;
          if (isNaN(settings._groupMinRewardPercent ?? 0)) {
            settings._groupMinRewardPercent = null
          }

          settings._groupMaxRewardPercent = group.maxReward ? helper.floatToPercent(group.maxReward) : null;
          if (isNaN(settings._groupMaxRewardPercent ?? 0)) {
            settings._groupMaxRewardPercent = null
          }

          settings._rewardPercent = helper.floatToPercent(settings.reward);
          if (isNaN(settings._rewardPercent)) {
            settings._rewardPercent = null
          }
        }
        state.rewardSettingsByGroupId = rewardSettingsByGroupId
      } finally {
        modalLoader.value?.hide()
      }
    })

    const sortedSettings = computed<TeamLeadRewardSettingsDTO[]>(() => {
      let items = Object.values(state.rewardSettingsByGroupId);

      items.sort((a: TeamLeadRewardSettingsDTO, b: TeamLeadRewardSettingsDTO) => {
        return a._trafficGroup!.type > b._trafficGroup!.type ? 1 : -1;
      })

      return items as TeamLeadRewardSettingsDTO[];
    })

    const isValidRewards = (item: TeamLeadRewardSettingsDTO) => {
      return (item._rewardPercent ?? 0) <= (item._groupMinRewardPercent ?? 0)
    }

    return {
      ...toRefs(state),
      modalLoader,
      store,
      helper,
      numberFormatter,
      save,
      Direction,
      sortedSettings,
      isValidRewards
    }
  }
})
</script>
